import { stepSkyRun, SKY_STEP } from '../../../packages/domain/src/75-sky-rider.mjs';

const reviews = new WeakMap();
const fields = ['input', 'simulation', 'prepare', 'audio', 'paint', 'steps'];
const capacity = 300;

/** Review is deliberately local and explicit; never enable telemetry on a public host. */
export function skyLoopReviewEnabled(canvas) {
  const url = new URL(canvas?.ownerDocument?.location?.href ?? 'https://invalid.example/');
  return (
    ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) &&
    url.searchParams.get('skyReview') === '1'
  );
}

/** Last300 synchronous frame costs. Paint includes HUD/update/render submission,
 * not browser compositing or GPU execution. Called only by the opt-in overlay. */
export function readSkyLoopTiming(canvas) {
  const samples = reviews.get(canvas);
  if (!samples?.count) return null;
  const result = { loopSamples: samples.count };
  for (const name of fields) {
    const values = Array.from(samples.values[name].subarray(0, samples.count)).sort(
      (a, b) => a - b,
    );
    result[name + 'P95'] = values[Math.ceil(values.length * 0.95) - 1];
  }
  return result;
}

/** Preserve the existing fixed-step sample→simulation→prepare→audio→paint order.
 * Disabled review allocates no sample buffers and never reads a timing clock.
 * Injected clock and step permit deterministic attribution tests without RAF. */
export function createSkyPlayerLoop(
  readCanvas,
  { clock = () => performance.now(), step = stepSkyRun } = {},
) {
  const enabled = skyLoopReviewEnabled(readCanvas());
  const samples = enabled
    ? {
        count: 0,
        cursor: 0,
        values: Object.fromEntries(fields.map((name) => [name, new Float32Array(capacity)])),
      }
    : null;
  let canvas = null,
    key = null,
    lastTime = null,
    costs = null;
  const measure = (name, action) => {
    if (!costs) return action();
    const before = clock();
    const value = action();
    costs[name] += clock() - before;
    return value;
  };
  return {
    begin(state) {
      if (!enabled) return;
      const current = readCanvas();
      if (current !== canvas) {
        if (canvas) reviews.delete(canvas);
        canvas = current;
        samples.count = samples.cursor = 0;
        reviews.set(canvas, samples);
      }
      costs = null;
      if (state.status !== 'playing' || canvas.ownerDocument?.visibilityState === 'hidden') return;
      const nextKey = `${state.sector}:${state.boss ? 'boss' : 'approach'}`;
      if (nextKey !== key || (lastTime !== null && state.time < lastTime))
        samples.count = samples.cursor = 0;
      key = nextKey;
      lastTime = state.time;
      costs = Object.fromEntries(fields.map((name) => [name, 0]));
    },
    tick(state, input, renderer, pulse, skipTraversal, reducedMotion) {
      const before = costs ? clock() : 0;
      const command = {
        ...input.sample(SKY_STEP),
        pulse,
        skipTraversal,
        reducedMotion,
        weaponMuzzles: state.traversal ? null : renderer?.weaponMuzzles?.(),
      };
      const sampled = costs ? clock() : 0;
      step(state, command);
      if (costs) {
        costs.input += sampled - before;
        costs.simulation += clock() - sampled;
        costs.steps++;
      }
    },
    finish(state, prepare, audio, paint) {
      if (!costs) {
        prepare();
        audio.update(state);
        paint();
        return;
      }
      measure('prepare', prepare);
      measure('audio', () => audio.update(state));
      measure('paint', paint);
      if (!costs) return;
      for (const name of fields) samples.values[name][samples.cursor] = costs[name];
      samples.count = Math.min(capacity, samples.count + 1);
      samples.cursor = (samples.cursor + 1) % capacity;
      costs = null;
    },
    dispose() {
      if (canvas) reviews.delete(canvas);
      costs = null;
    },
  };
}
